import React, { useState } from 'react';
import {
    Col,
    Row,
    Container,
    Nav,
    NavItem,
    NavLink,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
} from 'reactstrap'
import AccountPasswordResetForm from '../../components/Account/AccountPasswordReset';
import AccountPasswordChangeForm from '../../components/Account/AccountPasswordChange';
import { AuthUserContext, withAuthorization, withOrganization } from '../../components/Session';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { withFirebase } from '../../components/Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { useAuthStore } from '../../store/auth.store';
import {
  getNotificationPreferences,
  saveNotificationPreferences,
} from '../../services/notification.service';
import {
  NotificationPreferences,
  NotificationType,
  NOTIFICATION_TYPES_ORDER,
  NOTIFICATION_LABELS,
  NOTIFICATION_DESCRIPTIONS,
  defaultPreferences,
} from '../../services/notificationPreferences';

const AccountPage = ({ firebase }: any) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [ipiNumber, setIpiNumber] = useState('');
    const [ipiSaving, setIpiSaving] = useState(false);
    const [ipiSaveSuccess, setIpiSaveSuccess] = useState(false);
    const [ipiSaveError, setIpiSaveError] = useState('');
    const [ipiLoaded, setIpiLoaded] = useState(false);

    // Notification preferences (per-type × per-channel matrix)
    const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences>(defaultPreferences());
    const [notifLoaded, setNotifLoaded] = useState(false);
    const [notifSaving, setNotifSaving] = useState(false);
    const [notifSaveSuccess, setNotifSaveSuccess] = useState(false);

    // Display preferences
    const [displayPrefs, setDisplayPrefs] = useState<Record<string, boolean>>({ hideMnemonics: true, hideLicensed: true });
    const [displayLoaded, setDisplayLoaded] = useState(false);
    const [displaySaving, setDisplaySaving] = useState(false);
    const [displaySaveSuccess, setDisplaySaveSuccess] = useState(false);

    const currentOrgId = useOrganizationStore(state => state.currentOrgId);
    const currentOrgName = useOrganizationStore(state => state.currentOrgName);
    const setStoreDisplayPreferences = useAuthStore(state => state.setDisplayPreferences);

    const toggle = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    // Load IPI number from user document
    const loadIpiNumber = async (userId: string) => {
        if (ipiLoaded) return;
        try {
            const db = getFirestore();
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setIpiNumber(userData.ipiNumber || '');
            }
            setIpiLoaded(true);
        } catch (error) {
            console.error('Error loading IPI number:', error);
        }
    };

    // Save IPI number to user document
    const saveIpiNumber = async (userId: string) => {
        setIpiSaving(true);
        setIpiSaveSuccess(false);
        setIpiSaveError('');
        try {
            const db = getFirestore();
            await updateDoc(doc(db, 'users', userId), {
                ipiNumber: ipiNumber
            });
            setIpiSaveSuccess(true);
            setTimeout(() => setIpiSaveSuccess(false), 3000);
        } catch (error: any) {
            console.error('Error saving IPI number:', error);
            setIpiSaveError(error.message || 'Failed to save IPI number');
        } finally {
            setIpiSaving(false);
        }
    };

    // Load notification preferences from member document (merged with defaults).
    const loadNotifPrefs = async (userId: string) => {
        if (notifLoaded || !currentOrgId || !firebase) return;
        try {
            const prefs = await getNotificationPreferences(firebase, currentOrgId, userId);
            setNotifPrefs(prefs);
            setNotifLoaded(true);
        } catch (error) {
            console.error('Error loading notification preferences:', error);
            setNotifLoaded(true);
        }
    };

    // Save notification preferences (full new-shape object).
    const saveNotifPrefs = async (userId: string) => {
        if (!currentOrgId || !firebase) return;
        setNotifSaving(true);
        setNotifSaveSuccess(false);
        try {
            await saveNotificationPreferences(firebase, currentOrgId, userId, notifPrefs);
            setNotifSaveSuccess(true);
            setTimeout(() => setNotifSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving notification preferences:', error);
        } finally {
            setNotifSaving(false);
        }
    };

    // Toggle a single channel value for one notification type.
    const toggleNotifPref = (type: NotificationType, channel: 'inApp' | 'email') => {
        setNotifPrefs(prev => ({
            ...prev,
            [channel]: { ...prev[channel], [type]: !prev[channel][type] },
        }));
    };

    // Load display preferences from member document
    const loadDisplayPrefs = async (userId: string) => {
        if (displayLoaded || !currentOrgId || !firebase) return;
        try {
            const prefs = await firebase.getMemberDisplayPreferences(currentOrgId, userId);
            setDisplayPrefs(prefs);
            setDisplayLoaded(true);
        } catch (error) {
            console.error('Error loading display preferences:', error);
            setDisplayLoaded(true);
        }
    };

    // Save display preferences
    const saveDisplayPrefs = async (userId: string) => {
        if (!currentOrgId || !firebase) return;
        setDisplaySaving(true);
        setDisplaySaveSuccess(false);
        try {
            await firebase.updateMemberDisplayPreferences(currentOrgId, userId, displayPrefs);
            // Update the global store so FindAudio picks it up immediately
            setStoreDisplayPreferences(displayPrefs as any);
            setDisplaySaveSuccess(true);
            setTimeout(() => setDisplaySaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving display preferences:', error);
        } finally {
            setDisplaySaving(false);
        }
    };

    const renderTabContent = (authUser: any) => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div>
                        <h4 className="mb-4">Account Overview</h4>
                        <div className="mb-3">
                            <p className="mb-2"><strong>User Name:</strong> {authUser.username}</p>
                            <p className="mb-2"><strong>Email:</strong> {authUser.email}</p>
                        </div>
                        <p className="text-muted">Manage your account settings and preferences.</p>
                    </div>
                );
            case 'password':
                return (
                    <div>
                        <h4 className="mb-4">Password Management</h4>
                        <Row>
                            <Col md="6">
                                <div className="mb-4">
                                    <AccountPasswordChangeForm />
                                </div>
                                <div>
                                    <AccountPasswordResetForm />
                                </div>
                            </Col>
                            <Col md="6">
                                {/* Space for additional fields on the right */}
                            </Col>
                        </Row>
                    </div>
                );
            case 'settings':
                // Load IPI number and display prefs when settings tab is accessed
                if (!ipiLoaded && authUser?.uid) {
                    loadIpiNumber(authUser.uid);
                }
                if (!displayLoaded && authUser?.uid) {
                    loadDisplayPrefs(authUser.uid);
                }
                return (
                    <div>
                        <h4 className="mb-4">Account Settings</h4>

                        {/* Library Display Preferences */}
                        <div className="mb-5">
                            <h5 className="mb-3">Library Display</h5>
                            {currentOrgName && (
                                <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                                    Control which tracks are visible by default in your library for <strong>{currentOrgName}</strong>.
                                </p>
                            )}
                            <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button
                                    type="button"
                                    onClick={() => setDisplayPrefs(prev => ({ ...prev, hideMnemonics: !prev.hideMnemonics }))}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: '36px', minWidth: '36px', height: '20px', borderRadius: '10px', border: 'none',
                                        backgroundColor: displayPrefs.hideMnemonics ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)',
                                        cursor: 'pointer', transition: 'background-color 0.2s', position: 'relative', padding: 0,
                                    }}
                                >
                                    <div style={{
                                        width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fff',
                                        position: 'absolute', left: displayPrefs.hideMnemonics ? '18px' : '2px',
                                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    }} />
                                </button>
                                <div>
                                    <span style={{ color: '#fff' }}>Hide Mnemonics by default</span>
                                    <small className="text-muted d-block mt-1">
                                        Mnemonic tracks (jingles, stings, audio branding) are hidden unless you explicitly search for them.
                                    </small>
                                </div>
                            </div>
                            <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <button
                                    type="button"
                                    onClick={() => setDisplayPrefs(prev => ({ ...prev, hideLicensed: !prev.hideLicensed }))}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: '36px', minWidth: '36px', height: '20px', borderRadius: '10px', border: 'none',
                                        backgroundColor: displayPrefs.hideLicensed ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)',
                                        cursor: 'pointer', transition: 'background-color 0.2s', position: 'relative', padding: 0,
                                    }}
                                >
                                    <div style={{
                                        width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fff',
                                        position: 'absolute', left: displayPrefs.hideLicensed ? '18px' : '2px',
                                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    }} />
                                </button>
                                <div>
                                    <span style={{ color: '#fff' }}>Hide Licensed tracks by default</span>
                                    <small className="text-muted d-block mt-1">
                                        Tracks with active licenses are hidden to prevent accidental use on other projects.
                                    </small>
                                </div>
                            </div>
                            <Button
                                className="btn-info-custom"
                                onClick={() => saveDisplayPrefs(authUser.uid)}
                                disabled={displaySaving}
                            >
                                <span style={{ position: 'relative', zIndex: 2 }}>{displaySaving ? 'Saving...' : 'Save Display Preferences'}</span>
                            </Button>
                            {displaySaveSuccess && (
                                <Alert color="success" className="mt-3 mb-0">
                                    Display preferences saved!
                                </Alert>
                            )}
                        </div>

                        <hr style={{ borderColor: 'rgba(255, 255, 255, 0.1)', marginBottom: '2rem' }} />

                        {/* Music Rights Information */}
                        <Row>
                            <Col md="6">
                                <div className="mb-4">
                                    <h5 className="mb-3">Music Rights Information</h5>
                                    <FormGroup>
                                        <Label for="ipiNumber">IPI Number (Interested Parties Information)</Label>
                                        <Input
                                            type="text"
                                            id="ipiNumber"
                                            value={ipiNumber}
                                            onChange={(e) => setIpiNumber(e.target.value)}
                                            placeholder="Enter your 9-11 digit IPI number"
                                            className="mb-2"
                                        />
                                        <small className="text-muted d-block mb-3">
                                            Your unique identifier assigned by SAMRO/CISAC as a songwriter, composer, or publisher.
                                        </small>
                                        <Button
                                            className="btn-info-custom"
                                            onClick={() => saveIpiNumber(authUser.uid)}
                                            disabled={ipiSaving}
                                        >
                                            <span style={{ position: 'relative', zIndex: 2 }}>{ipiSaving ? 'Saving...' : 'Save IPI Number'}</span>
                                        </Button>
                                        {ipiSaveSuccess && (
                                            <Alert color="success" className="mt-3 mb-0">
                                                IPI number saved successfully!
                                            </Alert>
                                        )}
                                        {ipiSaveError && (
                                            <Alert color="danger" className="mt-3 mb-0">
                                                {ipiSaveError}
                                            </Alert>
                                        )}
                                    </FormGroup>
                                </div>
                            </Col>
                            <Col md="6">
                                <div className="p-3" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                    <h6 className="mb-2">About IPI Numbers</h6>
                                    <p className="text-muted small mb-2">
                                        The IPI (Interested Parties Information) number is an internationally recognized identifier
                                        for songwriters, composers, and publishers.
                                    </p>
                                    <p className="text-muted small mb-0">
                                        In South Africa, you receive this through your registration with SAMRO
                                        (Southern African Music Rights Organisation). This number will be used when
                                        registering your works with SAMRO and RISA.
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            case 'notifications':
                // Load notification preferences when tab is accessed
                if (!notifLoaded && authUser?.uid) {
                    loadNotifPrefs(authUser.uid);
                }
                return (
                    <div>
                        <h4 className="mb-4">Notification Preferences</h4>
                        {currentOrgName && (
                            <p className="text-muted mb-4">
                                Manage which notifications you receive for <strong>{currentOrgName}</strong>.
                            </p>
                        )}
                        <Row>
                            <Col md="8">
                                <div className="mb-4">
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 90px 90px',
                                            alignItems: 'center',
                                            gap: '12px 16px',
                                            padding: '12px 16px',
                                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                                            fontSize: '0.85rem',
                                            color: 'rgba(255,255,255,0.6)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        <div>Notification</div>
                                        <div style={{ textAlign: 'center' }}>In app</div>
                                        <div style={{ textAlign: 'center' }}>Email</div>
                                    </div>

                                    {NOTIFICATION_TYPES_ORDER.map((type) => (
                                        <div
                                            key={type}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 90px 90px',
                                                alignItems: 'center',
                                                gap: '12px 16px',
                                                padding: '16px',
                                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                            }}
                                        >
                                            <div>
                                                <div style={{ color: '#fff', fontWeight: 500 }}>
                                                    {NOTIFICATION_LABELS[type]}
                                                </div>
                                                <small className="text-muted d-block mt-1">
                                                    {NOTIFICATION_DESCRIPTIONS[type]}
                                                </small>
                                            </div>
                                            {(['inApp', 'email'] as const).map((channel) => {
                                                const checked = notifPrefs[channel][type];
                                                return (
                                                    <div key={channel} style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleNotifPref(type, channel)}
                                                            aria-label={`${channel === 'inApp' ? 'In-app' : 'Email'} ${NOTIFICATION_LABELS[type]}`}
                                                            style={{
                                                                width: '36px',
                                                                minWidth: '36px',
                                                                height: '20px',
                                                                borderRadius: '10px',
                                                                border: 'none',
                                                                backgroundColor: checked
                                                                    ? 'var(--accent-1-color)'
                                                                    : 'rgba(255, 255, 255, 0.2)',
                                                                cursor: 'pointer',
                                                                transition: 'background-color 0.2s',
                                                                position: 'relative',
                                                                padding: 0,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    width: '16px',
                                                                    height: '16px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#fff',
                                                                    position: 'absolute',
                                                                    left: checked ? '18px' : '2px',
                                                                    top: '2px',
                                                                    transition: 'left 0.2s',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                                }}
                                                            />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}

                                    <div className="mt-4">
                                        <Button
                                            className="btn-info-custom"
                                            onClick={() => saveNotifPrefs(authUser.uid)}
                                            disabled={notifSaving}
                                        >
                                            <span style={{ position: 'relative', zIndex: 2 }}>
                                                {notifSaving ? 'Saving...' : 'Save Preferences'}
                                            </span>
                                        </Button>
                                        {notifSaveSuccess && (
                                            <Alert color="success" className="mt-3 mb-0">
                                                Notification preferences saved!
                                            </Alert>
                                        )}
                                    </div>
                                </div>
                            </Col>
                            <Col md="4">
                                <div className="p-3" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                    <h6 className="mb-2">About Notifications</h6>
                                    <p className="text-muted small mb-2">
                                        <strong>In-app</strong> notifications appear via the bell icon and pop-up modals
                                        while you're using Music Vault.
                                    </p>
                                    <p className="text-muted small mb-2">
                                        <strong>Email</strong> notifications are sent to your account address when
                                        the relevant event occurs.
                                    </p>
                                    <p className="text-muted small mb-0">
                                        Preferences apply to your membership in the current organization. Switching
                                        organizations may show different settings based on your role.
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <AuthUserContext.Consumer>
            {(authUser: any) => (
                <div className="wrapper pt-3 pb-3">
                    <Container fluid style={{paddingLeft: '100px', paddingRight: '100px'}}>
                        <Row>
                            <Col md="2" lg="2" className="pr-3">
                                <div className="mb-4">
                                    <h4 className="d-flex flex-row align-items-center mb-2">
                                        <i className="tim-icons icon-single-02 accent-1-icon mr-2"></i> 
                                        {authUser.username}
                                    </h4>
                                </div>
                                
                                <Nav vertical className="nav-pills">
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'overview' ? 'active' : ''}
                                            onClick={() => toggle('overview')}
                                        >
                                            <i className="tim-icons icon-chart-pie-36 mr-2"></i>
                                            Overview
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'password' ? 'active' : ''}
                                            onClick={() => toggle('password')}
                                        >
                                            <i className="tim-icons icon-lock-circle mr-2"></i>
                                            Password
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'settings' ? 'active' : ''}
                                            onClick={() => toggle('settings')}
                                        >
                                            <i className="tim-icons icon-settings mr-2"></i>
                                            Settings
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'notifications' ? 'active' : ''}
                                            onClick={() => toggle('notifications')}
                                        >
                                            <i className="tim-icons icon-bell-55 mr-2"></i>
                                            Notifications
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </Col>
                            
                            <Col md="10" lg="10">
                                <div className="card">
                                    <div className="card-body">
                                        {renderTabContent(authUser)}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            )}
        </AuthUserContext.Consumer>
    );
};

const condition = (authUser: any) => !!authUser;
export default withOrganization(withAuthorization(condition)(withFirebase(AccountPage)));